import React, { useRef, useState, useEffect } from "react";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Grid } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/browser";
import { call } from "services/axiosService/core";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";
import Loader from "pages/loader/Loader";
import {
  Checkbox,
  FormLabel,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
} from "@mui/material";

const ManageSubscription = ({
  openSubscriptionDialog,
  setOpenSubscriptionDialog,
  setOpenSnackbar,
  setSeverity,
  setAlertMessage,
  setSessionExpired,
}) => {
  const [controller] = useMaterialUIController();
  const { persona, beta, profile } = controller;
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Set categories based on persona
  useEffect(() => {
    switch (persona) {
      case "athlete":
        setCategories(["login_reminder", "assessment_reminder", "maxwell_reminder"]);
        break;
      case "guardian":
        setCategories([
          "login_reminder",
          "child/athlete_signup_reminder",
          "child/athlete_assessment_related",
          "payment_reminder",
          "learners_weekly_report",
        ]);
        break;
      case "adultAthlete":
        setCategories([
          "login_reminder",
          "assessment_reminder",
          "maxwell_reminder",
          "child/athlete_signup_reminder",
          "child/athlete_assessment_related",
          "payment_reminder",
          "learners_weekly_report",
        ]);
        break;
      case "admin":
        setCategories(["login_reminder"]);
        break;
      case "contentManager":
        setCategories(["login_reminder"]);
        break;
      default:
        setCategories(["login_reminder"]);
        break;
    }
  }, []);

  // Fetch email preferences when categories change
  useEffect(() => {
    if (categories.length > 0) {
      getEmailPreferences();
    }
  }, [categories]);

  const getEmailPreferences = () => {
    call(`users/${profile.id}/emailpreferences`, "GET", {})
      .then((response) => {
        if (response.status === 200) {
          const pref = response["data"]["email_preferences"] || {};
          const initializedPreferences = pref;
          categories.forEach((category) => {
            initializedPreferences[category] = pref[category] ?? true; // Default to true
          });
          setPreferences(initializedPreferences);
          setPreferencesLoaded(true);
        } else if (response.status === 440) {
          setSessionExpired(true);
        }
      })
      .catch((error) => {
        Sentry.captureException(error, {
          extra: {
            endpoint: `users/${profile.id}/emailpreferences`,
            method: "GET",
          },
        });
        console.error("Error fetching email preferences:", error);
      });
  };

  const handleToggle = (category) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category], // Toggle the value
    }));
  };

  const handleSave = () => {
    call(`users/${profile.id}/emailpreferences`, "PUT", { body: preferences })
      .then((response) => {
        if (response.status === 200) {
          setOpenSnackbar(true);
          setSeverity("success");
          setAlertMessage(t("alerts.profile.preferencesUpdated"));
          setOpenSubscriptionDialog(false);
        } else if (response.status === 440) {
          setSessionExpired(true);
        } else {
          setOpenSnackbar(true);
          setSeverity("success");
          setAlertMessage(t("alerts.profile.errorUpdatingPreferences"));
        }
      })
      .catch((error) => {
        Sentry.captureException(error, {
          extra: {
            endpoint: `users/${profile.id}/emailpreferences`,
            method: "PUT",
            data: { preferences },
          },
        });
        console.error("Error saving email preferences:", error);
        setOpenSnackbar(true);
        setSeverity("error");
        setAlertMessage(t("alerts.profile.errorUpdatingPreferences"));
      });
  };
  return (
    preferencesLoaded && (
      <Dialog
        open={openSubscriptionDialog}
        onClose={() => setOpenSubscriptionDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 2,
            py: 1,
          },
        }}
      >
        {/* Header with Close Icon */}
        <CloseIcon
          fontSize="small"
          onClick={() => setOpenSubscriptionDialog(false)}
          style={{
            color: "#6c757d",
            display: "block",
            marginLeft: "auto",
            marginRight: 4,
            marginTop: 4,
            cursor: "pointer",
          }}
        />

        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: "600",
            px: 3,
            pt: 2,
            pb: 1,
            mb: 2,
            borderBottom: "1px solid #e0e0e0",
            textAlign: "center",
          }}
        >
          <MDTypography variant="h5" sx={{ fontWeight: 600 }}>
            {t("headings.manageSubscription")}
          </MDTypography>
          <MDTypography variant="body2" sx={{ px: 2, mt: 0.5, color: "text.secondary" }}>
            {t("messages.profile.manageSubscription")}
          </MDTypography>
        </DialogTitle>

        {/* Preferences List */}
        <DialogContent sx={{ pt: 4 }}>
          <List dense>
            {categories.map((category) => (
              <ListItem key={category} disableGutters>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences[category] ?? true}
                      onChange={() => handleToggle(category)}
                      color="primary"
                    />
                  }
                  label={
                    <MDTypography variant="body2" fontWeight="medium">
                      {t(`emailPreferences.${category}`)}
                    </MDTypography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>

        {/* Save Button */}
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button
            onClick={handleSave}
            fullWidth
            sx={{
              backgroundColor: "#10B0D5",
              color: "#fff",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#089bb8",
                color: "#fff",
              },
              py: 1.25,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {t("buttonTitles.save")}
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

export default ManageSubscription;
